import { MathUtils, Quaternion, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CONFIG } from '../config.js';
import { centerOfMass } from '../physics/com.js';

const _tmp = new Vector3();
const _worldUp = new Vector3(0, 1, 0);
const _surfaceUp = new Vector3(0, 1, 0);
const _desiredUp = new Vector3(0, 1, 0);
const _smoothUp = new Vector3(0, 1, 0);
const _quat = new Quaternion();
const _fwd = new Vector3();
const _right = new Vector3();
const _look = new Vector3();
const _com = new Vector3();
const _desiredAim = new Vector3();
const _starDir = new Vector3();
const _smoothAim = new Vector3(0, 0, -1);

/**
 * Surface POV — stand on the day side and look at the three-body sky.
 *
 * Critical: surface "up" must face the stars (day hemisphere), NOT the night
 * side (planet-COM outward). Stars cluster near the system COM, so the old
 * outward-up put them under the ground most of the time.
 */
export class CameraRig {
  /**
   * @param {import('three').PerspectiveCamera} camera
   * @param {HTMLElement} domElement
   * @param {typeof CONFIG} [config]
   */
  constructor(camera, domElement, config = CONFIG) {
    this.camera = camera;
    this.domElement = domElement;
    this.config = config;
    this.state = 'orbit'; // orbit | transition | pov
    this.bodies = [];
    this.planet = null;
    this.orbitFov = camera.fov;
    this.povFov = config.POV_FOV || 88;

    this.controls = new OrbitControls(camera, domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.minDistance = 4;
    this.controls.maxDistance = 120;

    this._transition = null;
    this._focus = null;
    this._hasSmoothAim = false;
    this._hasSmoothUp = false;
    this._lastDt = 1 / 60;

    // user look offsets on top of auto sky aim
    this.povYaw = 0;
    this.povPitch = 0.02;
    this._dragging = false;
    this._lastX = 0;
    this._lastY = 0;

    this._onPointerDown = (e) => this._pointerDown(e);
    this._onPointerMove = (e) => this._pointerMove(e);
    this._onPointerUp = () => this._pointerUp();
    this._onKey = (e) => {
      if (e.key === 'Escape' && (this.state === 'pov' || this.state === 'transition')) {
        this.exitPov();
      }
    };

    domElement.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('keydown', this._onKey);
  }

  get mode() {
    return this.state;
  }

  get shouldCheckOffscreen() {
    return this.state === 'orbit';
  }

  get isSurfaceMode() {
    return this.state === 'pov' || (this.state === 'transition' && this._transition?.kind === 'toPov');
  }

  setBodies(bodies) {
    this.bodies = bodies;
    this.planet = bodies.find((b) => !b.isStar) || null;
  }

  resetOrbitDefault() {
    this.state = 'orbit';
    this._transition = null;
    this._focus = null;
    this._hasSmoothAim = false;
    this._hasSmoothUp = false;
    this.povYaw = 0;
    this.povPitch = 0.02;
    this._setFov(this.orbitFov);
    this.controls.enabled = true;
    this.camera.position.set(0, 8, 28);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
    this._setPlanetVisible(true);
  }

  enterPov() {
    if (!this.planet || this.state === 'pov') return;
    if (this.state === 'transition' && this._transition?.kind === 'toPov') return;
    this.controls.enabled = false;
    this._focus = null;
    this.povYaw = 0;
    this.povPitch = 0.02;
    this._hasSmoothAim = false;
    this._hasSmoothUp = false;
    this._setFov(this.povFov);

    const startPos = this.camera.position.clone();
    const startQuat = this.camera.quaternion.clone();
    this._computePovPose(_tmp, _quat, 0);
    this._transition = {
      kind: 'toPov',
      t: 0,
      duration: this.config.POV_TRANSITION_SEC,
      startPos,
      endPos: _tmp.clone(),
      startQuat,
      endQuat: _quat.clone(),
    };
    this.state = 'transition';
  }

  exitPov() {
    if (this.state === 'orbit') return;
    if (this.state === 'transition' && this._transition?.kind === 'toOrbit') return;
    this.controls.enabled = false;
    this._setPlanetVisible(true);
    this._hasSmoothAim = false;
    this._hasSmoothUp = false;

    const startPos = this.camera.position.clone();
    const startQuat = this.camera.quaternion.clone();
    const endPos = new Vector3(0, 8, 28);
    const endTarget = new Vector3(0, 0, 0);
    const endCam = this.camera.clone();
    endCam.position.copy(endPos);
    endCam.up.set(0, 1, 0);
    endCam.lookAt(endTarget);
    this._transition = {
      kind: 'toOrbit',
      t: 0,
      duration: this.config.POV_TRANSITION_SEC,
      startPos,
      endPos,
      startQuat,
      endQuat: endCam.quaternion.clone(),
      endTarget,
    };
    this.state = 'transition';
  }

  focusBody(body, duration = 0.8) {
    if (this.state !== 'orbit' || !body) return;
    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const endTarget = body.position.clone();
    const offset = startPos.clone().sub(startTarget);
    if (offset.lengthSq() < 1e-6) offset.set(0, 4, 12);
    offset.setLength(MathUtils.clamp(offset.length(), 6, 18));
    const endPos = endTarget.clone().add(offset);
    this._focus = {
      t: 0,
      duration,
      startPos,
      endPos,
      startTarget,
      endTarget,
    };
  }

  /**
   * Day-side surface normal: weighted direction from planet toward stars.
   * That puts the suns in the sky (near upper hemisphere), not underfoot.
   */
  _computeDesiredUp(out) {
    if (!this.planet) return out.set(0, 1, 0);
    const origin = this.planet.position;
    out.set(0, 0, 0);
    let sumW = 0;

    for (let i = 0; i < this.bodies.length; i++) {
      const b = this.bodies[i];
      if (!b.isStar) continue;
      _starDir.copy(b.position).sub(origin);
      const dist = _starDir.length();
      if (dist < 1e-4) continue;
      _starDir.multiplyScalar(1 / dist);
      // Prefer not-too-close suns for a stable day side; still count mass.
      let w = b.mass / (1 + dist * 0.15);
      if (dist < 0.7) w *= 0.25;
      out.addScaledVector(_starDir, w);
      sumW += w;
    }

    if (sumW < 1e-6) {
      // fallback: face system COM (stars usually near there)
      if (this.bodies.length) {
        _com.copy(centerOfMass(this.bodies));
        out.copy(_com).sub(origin);
        if (out.lengthSq() < 1e-8) out.copy(_worldUp);
        else out.normalize();
      } else {
        out.copy(_worldUp);
      }
    } else {
      out.multiplyScalar(1 / sumW);
      if (out.lengthSq() < 1e-8) out.copy(_worldUp);
      else out.normalize();
    }

    // tiny world-Y blend only when nearly flat to avoid gimbal flip
    if (Math.abs(out.y) < 0.08) out.lerp(_worldUp, 0.2).normalize();
    return out;
  }

  getSurfaceUp(out = new Vector3()) {
    this._computeDesiredUp(_desiredUp);
    if (!this._hasSmoothUp || !this.isSurfaceMode) {
      _smoothUp.copy(_desiredUp);
      this._hasSmoothUp = this.isSurfaceMode;
      return out.copy(_smoothUp);
    }
    // soft-slew ground orientation
    const maxRad = (this.config.POV_UP_SLEW_RAD_S || 0.35) * Math.max(this._lastDt, 1e-4);
    const angle = _smoothUp.angleTo(_desiredUp);
    if (angle < 1e-5) {
      _smoothUp.copy(_desiredUp);
    } else {
      const t = Math.min(1, maxRad / angle);
      _smoothUp.lerp(_desiredUp, t).normalize();
    }
    return out.copy(_smoothUp);
  }

  /**
   * Look direction among stars from the eye. With day-side up, stars sit in
   * the upper sky — weight them openly (no harsh below-horizon kill).
   */
  _computeDesiredSkyAim(eye, surfaceUp, out) {
    out.set(0, 0, 0);
    let sumW = 0;
    let best = null;
    let bestScore = -Infinity;

    for (let i = 0; i < this.bodies.length; i++) {
      const b = this.bodies[i];
      if (!b.isStar) continue;

      _starDir.copy(b.position).sub(eye);
      const dist = _starDir.length();
      if (dist < 1e-4) continue;
      _starDir.multiplyScalar(1 / dist);

      const elev = _starDir.dot(surfaceUp); // +zenith with day-side up

      let w = b.mass * (1.4 / (1 + dist * 0.18));
      // de-weight extreme flyby whip
      if (dist < 0.8) w *= 0.15;
      else if (dist < 1.5) w *= 0.45;
      // soft horizon: still include slightly below so we track rising suns
      if (elev < -0.25) w *= 0.08;
      else if (elev < 0) w *= 0.45;
      else w *= 0.85 + 0.4 * elev;

      out.addScaledVector(_starDir, w);
      sumW += w;

      const score = w * (1 + Math.max(elev, 0));
      if (score > bestScore) {
        bestScore = score;
        best = _starDir.clone();
      }
    }

    if (sumW < 1e-6) {
      // look along surface-forward default: slightly elevated along -world or toward COM
      if (this.bodies.length) {
        _com.copy(centerOfMass(this.bodies));
        out.copy(_com).sub(eye);
        if (out.lengthSq() < 1e-8) out.copy(surfaceUp);
        else out.normalize();
      } else {
        out.copy(surfaceUp);
      }
    } else {
      out.multiplyScalar(1 / sumW);
      if (out.lengthSq() < 1e-8) out.copy(best || surfaceUp);
      else out.normalize();
    }

    // Keep framing: horizon lower third, suns in upper half of frame
    const elev = out.dot(surfaceUp);
    if (elev < 0.18) {
      if (best && best.dot(surfaceUp) > elev) out.copy(best);
      const e2 = out.dot(surfaceUp);
      if (e2 < 0.2) out.addScaledVector(surfaceUp, 0.2 - e2).normalize();
    }
    // Don't stare straight at zenith forever — leave room for multiple suns
    const e3 = out.dot(surfaceUp);
    if (e3 > 0.78) out.addScaledVector(surfaceUp, -(e3 - 0.78) * 0.6).normalize();

    return out;
  }

  _smoothTowardAim(desired, dt) {
    if (!this._hasSmoothAim) {
      _smoothAim.copy(desired);
      this._hasSmoothAim = true;
      return _smoothAim;
    }
    const maxRad = (this.config.POV_AIM_SLEW_RAD_S || 0.65) * Math.max(dt, 1e-4);
    const angle = _smoothAim.angleTo(desired);
    if (angle < 1e-5) {
      _smoothAim.copy(desired);
      return _smoothAim;
    }
    const t = Math.min(1, maxRad / angle);
    _smoothAim.lerp(desired, t).normalize();
    return _smoothAim;
  }

  update(dt) {
    this._lastDt = dt;

    if (this.state === 'transition' && this._transition) {
      if (this._transition.kind === 'toPov' && this.planet) {
        this._computePovPose(_tmp, _quat, dt);
        this._transition.endPos.copy(_tmp);
        this._transition.endQuat.copy(_quat);
      }
      this._transition.t += dt;
      const u = Math.min(1, this._transition.t / this._transition.duration);
      const s = u * u * (3 - 2 * u);
      this.camera.position.lerpVectors(this._transition.startPos, this._transition.endPos, s);
      this.camera.quaternion.slerpQuaternions(
        this._transition.startQuat,
        this._transition.endQuat,
        s,
      );
      if (this._transition.kind === 'toPov') {
        this._setFov(MathUtils.lerp(this.orbitFov, this.povFov, s));
      } else {
        this._setFov(MathUtils.lerp(this.povFov, this.orbitFov, s));
      }

      if (u >= 1) {
        if (this._transition.kind === 'toPov') {
          this.state = 'pov';
          this._setPlanetVisible(false);
          this._setFov(this.povFov);
          this._transition = null;
        } else {
          this.state = 'orbit';
          this.controls.target.copy(this._transition.endTarget || new Vector3(0, 0, 0));
          this.controls.enabled = true;
          this._setFov(this.orbitFov);
          this._transition = null;
        }
      }
      return;
    }

    if (this.state === 'pov' && this.planet) {
      this._applyPovPose(dt);
      return;
    }

    if (this._focus) {
      this._focus.t += dt;
      const u = Math.min(1, this._focus.t / this._focus.duration);
      const s = u * u * (3 - 2 * u);
      this.camera.position.lerpVectors(this._focus.startPos, this._focus.endPos, s);
      this.controls.target.lerpVectors(this._focus.startTarget, this._focus.endTarget, s);
      if (u >= 1) this._focus = null;
    }

    if (this.state === 'orbit') this.controls.update();
  }

  _computePovPose(outPos, outQuat, dt = this._lastDt) {
    const planet = this.planet;
    // Day-side footing: up points toward the stellar sky
    this.getSurfaceUp(_surfaceUp);

    const eyeHeight = Math.max(this.config.POV_EYE_HEIGHT, 0.25) + planet.radius * 0.55;
    outPos.copy(planet.position).addScaledVector(_surfaceUp, eyeHeight);

    this._computeDesiredSkyAim(outPos, _surfaceUp, _desiredAim);
    const aim = this._smoothTowardAim(_desiredAim, dt);

    const elev = Math.asin(MathUtils.clamp(aim.dot(_surfaceUp), -1, 1));
    _fwd.copy(aim).addScaledVector(_surfaceUp, -aim.dot(_surfaceUp));
    if (_fwd.lengthSq() < 1e-8) {
      _fwd.crossVectors(_surfaceUp, _worldUp);
      if (_fwd.lengthSq() < 1e-8) _fwd.set(1, 0, 0);
    }
    _fwd.normalize();
    _right.crossVectors(_surfaceUp, _fwd).normalize();

    const cosY = Math.cos(this.povYaw);
    const sinY = Math.sin(this.povYaw);
    _fwd
      .set(
        _fwd.x * cosY + _right.x * sinY,
        _fwd.y * cosY + _right.y * sinY,
        _fwd.z * cosY + _right.z * sinY,
      )
      .normalize();
    _right.crossVectors(_surfaceUp, _fwd).normalize();

    // Default framing keeps suns high in the frame
    const pitch = MathUtils.clamp(elev + this.povPitch, 0.12, 0.85);
    _fwd.multiplyScalar(Math.cos(pitch)).addScaledVector(_surfaceUp, Math.sin(pitch)).normalize();

    _look.copy(outPos).add(_fwd);

    const cam = this.camera.clone();
    cam.position.copy(outPos);
    cam.up.copy(_surfaceUp);
    cam.lookAt(_look);
    outQuat.copy(cam.quaternion);
  }

  _applyPovPose(dt) {
    this._computePovPose(_tmp, _quat, dt);
    this.camera.position.copy(_tmp);
    this.camera.quaternion.copy(_quat);
  }

  _setFov(fov) {
    if (Math.abs(this.camera.fov - fov) < 0.01) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  _setPlanetVisible(visible) {
    if (!this.planet) return;
    this.planet.mesh.visible = visible;
    if (this.planet.glow) this.planet.glow.visible = visible;
    if (this.planet.trail) this.planet.trail.visible = visible;
  }

  _pointerDown(e) {
    if (this.state !== 'pov') return;
    if (e.button !== 0) return;
    this._dragging = true;
    this._lastX = e.clientX;
    this._lastY = e.clientY;
  }

  _pointerMove(e) {
    if (!this._dragging || this.state !== 'pov') return;
    const dx = e.clientX - this._lastX;
    const dy = e.clientY - this._lastY;
    this._lastX = e.clientX;
    this._lastY = e.clientY;
    this.povYaw -= dx * 0.0045;
    this.povPitch = MathUtils.clamp(this.povPitch - dy * 0.0035, -0.35, 0.4);
  }

  _pointerUp() {
    this._dragging = false;
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('keydown', this._onKey);
    this.controls.dispose();
  }
}
